import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect } from 'react';

// Shared container style for consistency
const VisualContainer = ({ children }: { children: React.ReactNode }) => (
    <div className="w-full aspect-[4/3] bg-gradient-to-br from-white to-blue-50/50 rounded-xl border border-blue-100 shadow-sm overflow-hidden relative flex items-center justify-center p-3 sm:p-6 md:p-8">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#3b82f60a_1px,transparent_1px),linear-gradient(to_bottom,#3b82f60a_1px,transparent_1px)] bg-[size:16px_16px] [mask-image:radial-gradient(ellipse_at_center,black_60%,transparent_100%)] pointer-events-none" />
        {children}
    </div>
);

// Component 1: Tone & Length Adjustment
export const ToneAdjustmentVisual = () => {
    const [isProfessional, setIsProfessional] = useState(false);

    useEffect(() => {
        const interval = setInterval(() => {
            setIsProfessional(prev => !prev);
        }, 4000); // Toggle every 4 seconds
        return () => clearInterval(interval);
    }, []);

    return (
        <VisualContainer>
            <div className="w-full max-w-[420px] h-[200px] sm:h-[320px] bg-white rounded-lg shadow-md border border-gray-100 p-3 sm:p-6 flex flex-col relative overflow-hidden">

                {/* Header / Tone Selector */}
                <div className="flex items-center justify-between mb-4 sm:mb-8 border-b border-gray-50 pb-4">
                    <div className="text-sm font-bold text-gray-800">Tone Adjuster</div>
                    <div className="flex bg-gray-50 p-1 rounded-lg">
                        <div className={`px-3 py-1 text-[10px] font-semibold rounded-md transition-all duration-300 ${!isProfessional ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-400'}`}>
                            Casual
                        </div>
                        <div className={`px-3 py-1 text-[10px] font-semibold rounded-md transition-all duration-300 ${isProfessional ? 'bg-blue-100 text-blue-700 shadow-sm' : 'text-gray-400'}`}>
                            Professional
                        </div>
                    </div>
                </div>

                {/* Text Content Area */}
                <div className="flex-1 relative">
                    <AnimatePresence mode='wait'>
                        {!isProfessional ? (
                            <motion.div
                                key="casual"
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 1.05 }}
                                transition={{ duration: 0.4 }}
                                className="bg-amber-50 rounded-lg p-4 border border-amber-100"
                            >
                                <div className="flex items-start gap-3">
                                    <div className="w-8 h-8 rounded-full bg-amber-200 flex items-center justify-center text-amber-700 text-xs font-bold">You</div>
                                    <div>
                                        <p className="text-xs text-gray-600 font-medium leading-relaxed italic">
                                            "Hey guys, we need to raise prices a bit cause costs are going up. Hope thats cool."
                                        </p>
                                    </div>
                                </div>
                            </motion.div>
                        ) : (
                            <motion.div
                                key="professional"
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 1.05 }}
                                transition={{ duration: 0.4 }}
                                className="bg-blue-50 rounded-lg p-4 border border-blue-100 shadow-sm"
                            >
                                <div className="flex items-start gap-3">
                                    <div className="w-8 h-8 rounded-full bg-blue-200 flex items-center justify-center text-blue-700 text-xs font-bold">AI</div>
                                    <div>
                                        <p className="text-xs text-gray-800 font-medium leading-relaxed">
                                            "Due to rising operational costs, a slight price adjustment is necessary to maintain service quality."
                                        </p>
                                        <div className="mt-2 flex gap-2">
                                            <span className="text-[9px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">Formal</span>
                                            <span className="text-[9px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">Concise</span>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                {/* AI Processing Status */}
                <div className="absolute bottom-3 sm:bottom-6 left-3 sm:left-6 flex items-center gap-2">
                    <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                        className="w-4 h-4 border-2 border-blue-200 border-t-blue-500 rounded-full"
                    />
                    <span className="text-[10px] text-gray-400 font-medium">{isProfessional ? "Refining tone..." : "Analyzing input..."}</span>
                </div>

            </div>
        </VisualContainer>
    );
};

// Component 2: Accurate Translation
export const TranslationVisual = () => {
    const [language, setLanguage] = useState<'EN' | 'ES'>('EN');

    useEffect(() => {
        const interval = setInterval(() => {
            setLanguage(prev => prev === 'EN' ? 'ES' : 'EN');
        }, 4000);
        return () => clearInterval(interval);
    }, []);

    return (
        <VisualContainer>
            <div className="w-full max-w-[420px] h-[200px] sm:h-[320px] bg-white rounded-lg shadow-md border border-gray-100 p-3 sm:p-6 flex flex-col relative overflow-hidden">

                {/* Header with Language Switcher */}
                <div className="flex justify-between items-center mb-3 sm:mb-6">
                    <div className="text-sm font-bold text-gray-800">Smart Translate</div>
                    <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg p-1">
                        <div className={`px-2 py-1 text-[9px] font-bold rounded ${language === 'EN' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-400'}`}>EN</div>
                        <svg className="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
                        <div className={`px-2 py-1 text-[9px] font-bold rounded ${language === 'ES' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-400'}`}>ES</div>
                    </div>
                </div>

                {/* Content Card */}
                <div className="flex-1 flex flex-col justify-center">
                    <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm relative overflow-hidden group">

                        {/* Globe Icon Background */}
                        <div className="absolute -right-4 -top-4 w-24 h-24 text-gray-50 opacity-50 pointer-events-none">
                            <svg fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" /></svg>
                        </div>

                        <AnimatePresence mode="wait">
                            <motion.div
                                key={language}
                                initial={{ opacity: 0, x: 10 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -10 }}
                                transition={{ duration: 0.4 }}
                                className="space-y-4 relative z-10"
                            >
                                {language === 'EN' ? (
                                    <>
                                        <div className="flex items-center gap-2 mb-2">
                                            <span className="text-[10px] font-bold bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">Financials</span>
                                        </div>
                                        <h4 className="text-lg font-bold text-gray-900">Q4 Revenue Growth</h4>
                                        <p className="text-sm text-gray-500 leading-relaxed">
                                            Our revenue grew by <strong className="text-blue-600">24%</strong> quarter-over-quarter, driven by strong enterprise adoption in key markets.
                                        </p>
                                    </>
                                ) : (
                                    <>
                                        <div className="flex items-center gap-2 mb-2">
                                            <span className="text-[10px] font-bold bg-green-100 text-green-700 px-1.5 py-0.5 rounded">Finanzas</span>
                                        </div>
                                        <h4 className="text-lg font-bold text-gray-900">Crecimiento de Ingresos T4</h4>
                                        <p className="text-sm text-gray-500 leading-relaxed">
                                            Nuestros ingresos crecieron un <strong className="text-blue-600">24%</strong> trimestre tras trimestre, impulsados por la adopción empresarial en mercados clave.
                                        </p>
                                    </>
                                )}
                            </motion.div>
                        </AnimatePresence>
                    </div>
                </div>

                {/* Progress Bar Loader Overlay */}
                <div className="absolute inset-x-3 sm:inset-x-6 bottom-3 sm:bottom-6 h-1 bg-gray-100 rounded-full overflow-hidden">
                    <motion.div
                        className="h-full bg-blue-500"
                        animate={{ width: ["0%", "100%", "0%"] }}
                        transition={{ duration: 4, ease: "easeInOut", times: [0, 0.5, 1], repeat: Infinity }}
                    />
                </div>

            </div>
        </VisualContainer>
    );
};
